import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Savings as SavingsIcon,
  AttachMoney as MoneyIcon,
  LocalOffer as DiscountIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { theme, commonStyles } from '../../styles/theme';
import { getConfig } from '../../lib/publicConfig';

const USD_PRICE_PER_NFT = getConfig('pricing.per_nft_usd', 0.20);
const CUSTOM_TOKEN_DISCOUNT = getConfig('custom_token.discount_percent', 10);
const STRIPE_PROCESSING_FEE = getConfig('stripe.processing_fee_pct', 3.0);

const StatCard = ({ icon: Icon, title, value, subtitle, color = '#646cff', isLoading }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.colors.borderPrimary}`,
          bgcolor: theme.colors.bgCard,
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: color,
            boxShadow: `0 4px 16px ${color}30`,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Icon sx={{ fontSize: 20, color }} />
          <Typography variant="body2" sx={{ color: theme.colors.textSecondary, fontWeight: 600, fontSize: '0.875rem' }}>
            {title}
          </Typography>
        </Box>
        {isLoading ? (
          <CircularProgress size={28} sx={{ color }} />
        ) : (
          <>
            <Typography variant="h4" sx={{ fontWeight: 900, color, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: theme.colors.textSecondary, fontSize: '0.75rem' }}>
                {subtitle}
              </Typography>
            )}
          </>
        )}
      </Box>
    </motion.div>
  );
};

const ANIMATED_PRICE_COST = getConfig('pricing.animated_cost', 2.00);

const PricingCalculator = ({ conversionRates = {}, pricesLoading = false, pricesError = null }) => {
  const [collectionSize, setCollectionSize] = useState(10000);
  const [paymentMethod, setPaymentMethod] = useState('sol');
  const [inputValue, setInputValue] = useState('10000');
  // Server-side generation is now always used (no client-side option)
  const generationType = 'server'; // Always server-side
  const [collectionType, setCollectionType] = useState('static'); // 'static', 'animated', or 'mixed'
  const [animatedCount, setAnimatedCount] = useState(0); // For mixed collections

  const handleSizeChange = (event, newValue) => {
    if (typeof newValue === 'number') {
      setCollectionSize(newValue);
      setInputValue(newValue.toString());
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 100 && numValue <= 50000) {
      setCollectionSize(numValue);
    }
  };

  const calculations = useMemo(() => {
    let baseUsdCost = 0;
    
    // Calculate based on collection type
    if (collectionType === 'animated') {
      // Animated collections use fixed cost per NFT
      baseUsdCost = collectionSize * ANIMATED_PRICE_COST;
    } else if (collectionType === 'mixed') {
      // Mixed collections: static NFTs + animated NFTs
      const staticCount = collectionSize - animatedCount;
      const staticCost = staticCount * USD_PRICE_PER_NFT; // Base price is now server-side price
      const animatedCost = animatedCount * ANIMATED_PRICE_COST;
      baseUsdCost = staticCost + animatedCost;
    } else {
      // Static collections - base price is now server-side price (no multiplier needed)
      baseUsdCost = collectionSize * USD_PRICE_PER_NFT;
    }
    
    const isCustomToken = paymentMethod === 'deads';
    const discountAmount = isCustomToken ? (baseUsdCost * CUSTOM_TOKEN_DISCOUNT) / 100 : 0;
    const discountedUsdCost = baseUsdCost - discountAmount;
    const stripeFee = paymentMethod === 'card' ? (baseUsdCost * STRIPE_PROCESSING_FEE) / 100 : 0;
    const finalUsdCost = paymentMethod === 'card' ? baseUsdCost + stripeFee : discountedUsdCost;
    const solAmount = conversionRates.SOL > 0 ? finalUsdCost / conversionRates.SOL : 0;
    const ethAmount = conversionRates.ETH > 0 ? finalUsdCost / conversionRates.ETH : 0;
    const deadsAmount = conversionRates.DEADS > 0 ? discountedUsdCost / conversionRates.DEADS : 0;
    const savingsVsCard = paymentMethod !== 'card' ? stripeFee + (isCustomToken ? discountAmount : 0) : 0;
    const savingsPercent = savingsVsCard > 0 ? (savingsVsCard / baseUsdCost) * 100 : 0;
    
    return {
      baseUsdCost,
      discountAmount,
      discountedUsdCost,
      stripeFee,
      finalUsdCost,
      solAmount,
      ethAmount,
      deadsAmount,
      savingsVsCard,
      savingsPercent,
      perNftCost: finalUsdCost / collectionSize,
    };
  }, [collectionSize, paymentMethod, conversionRates, generationType, collectionType, animatedCount]);

  const paymentMethods = [
    { value: 'sol', label: 'SOL', icon: '◎', color: '#14F195' },
    { value: 'eth', label: 'ETH', icon: 'Ξ', color: '#627EEA' },
    { value: 'deads', label: 'DEADS', icon: '💀', color: theme.colors.textPrimary },
    { value: 'card', label: 'Card', icon: '💳', color: '#646cff' },
  ];

  const getPaymentAmount = () => {
    switch (paymentMethod) {
      case 'sol':
        return calculations.solAmount > 0
          ? `${calculations.solAmount.toFixed(4)} SOL`
          : '—';
      case 'eth':
        return calculations.ethAmount > 0
          ? `${calculations.ethAmount.toFixed(6)} ETH`
          : '—';
      case 'deads':
        return calculations.deadsAmount > 0
          ? `${calculations.deadsAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })} DEADS`
          : '—';
      case 'card':
        return `$${calculations.finalUsdCost.toFixed(2)}`;
      default:
        return 'N/A';
    }
  };

  const priceForSelected = paymentMethod === 'sol'
    ? conversionRates.SOL
    : paymentMethod === 'eth'
      ? conversionRates.ETH
      : paymentMethod === 'deads'
        ? conversionRates.DEADS
        : null;
  const livePriceMissing = paymentMethod !== 'card' && !pricesLoading && !(priceForSelected > 0);

  const selectedMethod = paymentMethods.find(m => m.value === paymentMethod);

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
        <Typography variant="h3" sx={{ fontWeight: 800, color: theme.colors.textPrimary, mb: 2 }}>
          Calculate Your Cost
        </Typography>
        <Typography variant="body1" sx={{ color: theme.colors.textSecondary, maxWidth: 700, mx: 'auto', lineHeight: 1.7 }}>
          Transparent pricing with no hidden fees. Pay only for what you export.
        </Typography>
      </Box>

      <Box sx={{ ...commonStyles.card, borderRadius: 2, backdropFilter: 'blur(10px)', mb: { xs: 2, md: 3 }, p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: { xs: 2, md: 3 }, color: '#fff', fontSize: { xs: '1rem', md: '1.1rem' } }}>
          Collection Size
        </Typography>
        
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
          {[100, 500, 1000, 5000, 10000, 25000, 50000].map((size) => (
            <Grid item xs={6} sm={4} md={2.4} key={size}>
              <Button
                fullWidth
                variant={collectionSize === size ? 'contained' : 'outlined'}
                onClick={() => {
                  setCollectionSize(size);
                  setInputValue(size.toString());
                }}
                sx={{
                  py: 1.5,
                  bgcolor: collectionSize === size ? '#646cff' : 'transparent',
                  borderColor: collectionSize === size ? '#646cff' : 'divider',
                  color: collectionSize === size ? '#fff' : 'text.secondary',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: collectionSize === size ? '#5359d9' : 'rgba(100, 108, 255, 0.1)',
                    borderColor: '#646cff'
                  }
                }}
              >
                {size >= 1000 ? `${size / 1000}K` : size}
              </Button>
            </Grid>
          ))}
          <Grid item xs={12}>
            <TextField
              fullWidth
              value={inputValue}
              onChange={handleInputChange}
              type="number"
              label="Custom Amount"
              placeholder="Enter custom number (100-50,000)"
              InputProps={{ 
                inputProps: { min: 100, max: 50000, step: 100 },
                sx: { color: '#fff', fontSize: '1.1rem', fontWeight: 600 }
              }}
              InputLabelProps={{ sx: { color: theme.colors.textSecondary } }}
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: '#646cff' },
                  '&.Mui-focused fieldset': { borderColor: '#646cff' }
                }
              }}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ 
          p: 2.5, 
          bgcolor: 'rgba(39, 48, 63, 0.4)', 
          borderRadius: 2, 
          border: `1px solid ${theme.colors.borderPrimary}`,
          textAlign: 'center'
        }}>
          <Typography variant="h3" sx={{ fontWeight: 900, color: theme.colors.primary, mb: 0.5 }}>
            {collectionSize.toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.textSecondary, fontSize: '0.875rem' }}>
            NFTs Selected
          </Typography>
        </Box>
      </Box>

      {/* Server-Side Generation Info (Always Enabled) */}
      <Box sx={{ ...commonStyles.card, borderRadius: 2, backdropFilter: 'blur(10px)', mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#fff', fontSize: '1.1rem' }}>
          Generation Type
        </Typography>
        <Alert severity="info">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            🚀 Server-Side Generation (Always Enabled)
          </Typography>
          <Typography variant="caption" display="block">
            All generation uses server-side rendering for maximum speed, reliability, and quality. Handles collections up to 50K+ NFTs with parallel processing and includes steganography watermarking.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600 }}>
            Pricing: ${USD_PRICE_PER_NFT.toFixed(2)} per static NFT, $2.00 per animated NFT
          </Typography>
        </Alert>
      </Box>

      <Box sx={{ ...commonStyles.card, borderRadius: 2, backdropFilter: 'blur(10px)', mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#fff', fontSize: '1.1rem' }}>
          Collection Type
        </Typography>
        <ToggleButtonGroup 
          value={collectionType} 
          exclusive 
          onChange={(e, newValue) => {
            if (newValue) {
              setCollectionType(newValue);
              if (newValue !== 'mixed') setAnimatedCount(0);
              if (newValue === 'animated' || newValue === 'mixed') setGenerationType('server');
            }
          }} 
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="static">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>🖼️ Static</Typography>
              <Typography variant="caption" sx={{ color: theme.colors.textSecondary }}>PNG/JPG/WebP</Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="animated">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>🎬 Animated</Typography>
              <Typography variant="caption" sx={{ color: theme.colors.textSecondary }}>MP4/WebM/GIF</Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="mixed">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>🎭 Mixed</Typography>
              <Typography variant="caption" sx={{ color: theme.colors.textSecondary }}>Static + Animated</Typography>
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
        
        {collectionType === 'mixed' && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              value={animatedCount}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                if (value >= 0 && value <= collectionSize) {
                  setAnimatedCount(value);
                }
              }}
              type="number"
              label="Animated NFT Count"
              placeholder="How many animated NFTs?"
              InputProps={{ 
                inputProps: { min: 0, max: collectionSize, step: 1 },
                sx: { color: '#fff', fontSize: '1rem', fontWeight: 600 }
              }}
              InputLabelProps={{ sx: { color: theme.colors.textSecondary } }}
              helperText={`Static: ${collectionSize - animatedCount} | Animated: ${animatedCount}`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: '#dc2626' },
                  '&.Mui-focused fieldset': { borderColor: '#dc2626' }
                }
              }}
            />
          </Box>
        )}
        
        {collectionType === 'static' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Static NFTs use standard image formats (PNG, JPG, WebP). Base pricing applies.
          </Alert>
        )}
        {collectionType === 'animated' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Animated NFTs cost ${ANIMATED_PRICE_COST.toFixed(2)} each (fixed cost). Server-side generation required.
          </Alert>
        )}
        {collectionType === 'mixed' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Mixed collections: Static NFTs use base pricing, animated NFTs cost ${ANIMATED_PRICE_COST.toFixed(2)} each.
          </Alert>
        )}
      </Box>

      <Box sx={{ ...commonStyles.card, borderRadius: 2, backdropFilter: 'blur(10px)', mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#fff', fontSize: '1.1rem' }}>
          Payment Method
        </Typography>
        <ToggleButtonGroup value={paymentMethod} exclusive onChange={(e, newValue) => newValue && setPaymentMethod(newValue)} fullWidth>
          {paymentMethods.map((method) => (
            <ToggleButton key={method.value} value={method.value}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '2rem' }}>{method.icon}</Typography>
                <Typography sx={{ fontWeight: 700 }}>{method.label}</Typography>
                {method.value === 'deads' && (
                  <Chip label={`${CUSTOM_TOKEN_DISCOUNT}% OFF`} size="small" sx={{ bgcolor: '#00ff00', color: '#000', fontWeight: 700, fontSize: '0.65rem' }} />
                )}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {paymentMethod === 'card' && <Alert severity="info" sx={{ mt: 2 }}>Credit card payments include a {STRIPE_PROCESSING_FEE}% processing fee</Alert>}
        {paymentMethod === 'deads' && <Alert severity="success" sx={{ mt: 2 }}>Save {CUSTOM_TOKEN_DISCOUNT}% by paying with DEADS tokens!</Alert>}
        {livePriceMissing && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Live {paymentMethod.toUpperCase()} price {pricesError === 'warming' ? 'is warming up' : 'is temporarily unavailable'} — retrying. Switch to Card for a confirmed USD total.
          </Alert>
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={MoneyIcon} title="Total Cost" value={getPaymentAmount()} subtitle={`$${calculations.finalUsdCost.toFixed(2)} USD`} color={selectedMethod.color} isLoading={pricesLoading && paymentMethod !== 'card'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={CalculateIcon} title="Per NFT" value={`$${calculations.perNftCost.toFixed(4)}`} subtitle={`${collectionSize.toLocaleString()} NFTs`} color="#646cff" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={DiscountIcon} title="Discount" value={paymentMethod === 'deads' ? `${CUSTOM_TOKEN_DISCOUNT}%` : '0%'} subtitle={`$${calculations.discountAmount.toFixed(2)} saved`} color="#00ff00" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={SavingsIcon} title="vs Credit Card" value={`$${calculations.savingsVsCard.toFixed(2)}`} subtitle={calculations.savingsPercent > 0 ? `${calculations.savingsPercent.toFixed(1)}% savings` : 'No savings'} color="#ffaa00" />
        </Grid>
      </Grid>

      <Box sx={{ ...commonStyles.card, borderRadius: 2, backdropFilter: 'blur(10px)', p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#fff', fontSize: '1.1rem' }}>Cost Breakdown</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ color: theme.colors.textSecondary }}>
              {collectionType === 'static' ? 'Static NFTs' : 'Base Cost'} ({collectionSize.toLocaleString()} × ${USD_PRICE_PER_NFT.toFixed(2)})
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff' }}>${(collectionSize * USD_PRICE_PER_NFT).toFixed(2)}</Typography>
          </Box>
          {paymentMethod === 'deads' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ color: '#00ff00' }}>DEADS Token Discount ({CUSTOM_TOKEN_DISCOUNT}%)</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#00ff00' }}>-${calculations.discountAmount.toFixed(2)}</Typography>
            </Box>
          )}
          {paymentMethod === 'card' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ color: theme.colors.primary }}>Processing Fee ({STRIPE_PROCESSING_FEE}%)</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: theme.colors.primary }}>+${calculations.stripeFee.toFixed(2)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Final Total</Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: selectedMethod.color }}>{getPaymentAmount()}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography variant="body2" sx={{ color: theme.colors.textSecondary }}>USD Equivalent</Typography>
            <Typography variant="body2" sx={{ color: theme.colors.textSecondary }}>${calculations.finalUsdCost.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PricingCalculator;
